import { getAddress, isAddress } from 'ethers'

import { IContractNamesController } from '../../interfaces/contractNames'
import { IEventEmitterRegistryController } from '../../interfaces/eventEmitter'
import { Fetch } from '../../interfaces/fetch'
import {
  BUNDLED_DEX_ROUTER_INDEX,
  DEX_ROUTERS_ERROR_COOLDOWN_IN_MS,
  DEX_ROUTERS_TTL_IN_MS,
  DexRouterIndex,
  DexRoutersCache,
  DexRoutersStorage,
  fetchDexRouterIndex,
  getDexRouterKey
} from '../../services/dexRouters/dexRouters'
import wait from '../../utils/wait'
import EventEmitter from '../eventEmitter/eventEmitter'

// contract names, originally, are fetched from the contract code onchain
// for a contract to have the same address on multiple chains it must also have the same code
// and same name, thats why we do not need to store the chain on which the contract name was
// found. Nevertheless, we send the chainId to the relayer because it need it find the chain
// on which the contract is actually deployed to get the name
interface ContractNames {
  [address: string]: {
    address: string
    name: string | null
    isLoading: boolean
    updatedAt?: number
    retryAfter?: number
    error?: string
    /**
     * Where the name came from. 'dex' names are curated by our backend (the
     * DEX router registry), so they bypass `isUnderstandableName` — that filter
     * exists to hide raw on-chain contract names like "TransparentProxy", not
     * protocol names.
     */
    source?: 'dex' | 'relayer'
    /** Protocol logo, only for 'dex' names. */
    logoURI?: string
  }
}
type ContractNamesRelayerResponse =
  | {
      contracts: {
        [address: string]: {
          address: string
          name: string
        }
      }
    }
  | { error: string }

export const DEX_ROUTERS_STORAGE_KEY = 'dexRoutersCache'

export const PERSIST_NOT_FOUND_IN_MS = 1000 * 60 * 60 // 60 minutes
export const PERSIST_FAILED_IN_MS = 1000 * 60 * 2 // 2 minutes

export function isUnderstandableName(name: string): boolean {
  const forbiddenWords = ['Ambire', 'Identity', 'Safe', 'Proxy', 'Diamond']
  if (name.endsWith('able')) return false
  if (forbiddenWords.some((fw) => name.toLowerCase().includes(fw.toLowerCase()))) return false
  return true
}

/**
 * Contract Names controller - responsible for handling the lookup of address names.
 * Resolved names are saved in `contractNames` permanently, unless the lookup failed, then new
 * attempt will be made only after PERSIST_NOT_FOUND_IN_MS to avoid unnecessary lookups.
 */
export class ContractNamesController extends EventEmitter implements IContractNamesController {
  #debounceTime: number

  #fetch: Fetch

  #lastTimeScheduledFetch: number = 0

  #contractNames: ContractNames = {}

  #contractsPendingToBeFetched: { address: string; chainId: bigint }[] = []

  #storage?: DexRoutersStorage

  /** The DEX registry, indexed `${chainId}:${address}`. null = not loaded yet. */
  #dexRouters: DexRouterIndex | null = null

  #dexRoutersFetchedAt: number = 0

  /** Single-flight guard: every caller awaits the same in-flight load. */
  #dexRoutersLoad: Promise<void> | null = null

  #dexRoutersRetryAfter: number = 0

  /**
   * Which chain each address was asked about. Kept out of `#contractNames` (and
   * so out of the UI state) — it only exists so addresses looked up BEFORE the
   * registry arrived can be re-resolved once it does.
   */
  #chainIdByAddress: { [address: string]: bigint } = {}

  constructor({
    eventEmitterRegistry,
    fetch,
    storage,
    debounceTime = 100
  }: {
    eventEmitterRegistry?: IEventEmitterRegistryController
    fetch: Fetch
    storage?: DexRoutersStorage
    debounceTime?: number
  }) {
    super(eventEmitterRegistry)

    this.#fetch = fetch
    this.#storage = storage
    this.#debounceTime = debounceTime

    // Warm the registry up front so the first sign screen already has it. It is
    // one ~18KB request per day and it is deliberately not awaited: a failure
    // (the endpoint is dev-only today) must not hold up name lookups.
    this.#ensureDexRouters().catch(() => {})
  }

  /**
   * Load the DEX registry: memory -> storage (stale-while-revalidate) -> network.
   * At most one network request is ever in flight, and a failure is retried no
   * more often than DEX_ROUTERS_ERROR_COOLDOWN_IN_MS.
   */
  async #ensureDexRouters(): Promise<void> {
    const isFresh = this.#dexRouters && Date.now() - this.#dexRoutersFetchedAt < DEX_ROUTERS_TTL_IN_MS
    if (isFresh) return
    if (this.#dexRoutersLoad) return this.#dexRoutersLoad
    if (Date.now() < this.#dexRoutersRetryAfter) return

    this.#dexRoutersLoad = (async () => {
      // Serve whatever is on disk immediately (even if stale) so a cold start
      // renders names without waiting on the network, then refresh below.
      if (!this.#dexRouters && this.#storage) {
        try {
          const cached = (await this.#storage.get(DEX_ROUTERS_STORAGE_KEY, undefined)) as
            | DexRoutersCache
            | undefined
          if (cached?.index) {
            this.#dexRouters = cached.index
            this.#dexRoutersFetchedAt = cached.fetchedAt || 0
            this.#applyDexRoutersToKnownNames()
            if (Date.now() - this.#dexRoutersFetchedAt < DEX_ROUTERS_TTL_IN_MS) return
          }
        } catch (e: any) {
          // A corrupt cache must not block the network path.
        }
      }

      try {
        const index = await fetchDexRouterIndex(this.#fetch)
        this.#dexRouters = index
        this.#dexRoutersFetchedAt = Date.now()
        this.#dexRoutersRetryAfter = 0
        if (this.#storage) {
          await this.#storage.set(DEX_ROUTERS_STORAGE_KEY, {
            fetchedAt: this.#dexRoutersFetchedAt,
            index
          })
        }
        this.#applyDexRoutersToKnownNames()
      } catch (e: any) {
        // Expected on uat/prod until the endpoint ships there (404), and on any
        // offline start. Silent: the relayer lookup still provides names.
        this.#dexRoutersRetryAfter = Date.now() + DEX_ROUTERS_ERROR_COOLDOWN_IN_MS
      }
    })().finally(() => {
      this.#dexRoutersLoad = null
    })

    return this.#dexRoutersLoad
  }

  /**
   * The fetched registry first, then the routers bundled with the app, so a
   * router the backend does not list yet (or a cold start with the registry
   * unreachable) still gets a name.
   */
  #getDexRouterEntry(address: string, chainId: bigint) {
    const key = getDexRouterKey(chainId, address)
    return this.#dexRouters?.[key] || BUNDLED_DEX_ROUTER_INDEX[key]
  }

  /** Already showing this registry name — nothing to write or emit. */
  #hasDexName(address: string, name: string): boolean {
    const current = this.#contractNames[address]
    return current?.source === 'dex' && current.name === name
  }

  /** Upgrade names resolved (or still pending) before the registry landed. */
  #applyDexRoutersToKnownNames(): void {
    if (!this.#dexRouters) return

    let hasChanges = false
    Object.entries(this.#chainIdByAddress).forEach(([address, chainId]) => {
      const entry = this.#getDexRouterEntry(address, chainId)
      if (!entry) return
      // Compare names, not just the source: a bundled name set before the
      // registry loaded must still give way to the registry's own label.
      if (this.#hasDexName(address, entry.name)) return

      this.#contractNames[address] = {
        address,
        name: entry.name,
        isLoading: false,
        updatedAt: Date.now(),
        source: 'dex',
        ...(entry.logoURI ? { logoURI: entry.logoURI } : {})
      }
      hasChanges = true
    })

    if (hasChanges) this.emitUpdate()
  }

  get contractNames(): ContractNames {
    const toReturn = Object.entries(this.#contractNames).map(([address, v]) => {
      if (!v.name) return [address, v]
      if (v.source === 'dex') return [address, v]
      if (isUnderstandableName(v.name)) return [address, v]
      return [address, { ...v, name: undefined }]
    })
    return Object.fromEntries(toReturn)
  }

  get contractsPendingToBeFetched(): { address: string; chainId: bigint }[] {
    return this.#contractsPendingToBeFetched
  }

  async #batchFetchNames(): Promise<void> {
    // using a second variable to avoid race conditions in `contractsPendingToBeFetched`
    const contractsToFetch = this.#contractsPendingToBeFetched
    this.#contractsPendingToBeFetched = []
    this.emitUpdate()

    const url = `https://apis.zkxwallet.com/cena/api/v3/contracts/multiple?addresses=${contractsToFetch.map(
      ({ address }) => address
    )}&chainIds=${contractsToFetch.map(({ chainId }) => chainId)}`

    let failed = false
    const res: ContractNamesRelayerResponse = await this.#fetch(url)
      .then((r) => r.json())
      .catch((e: any) => {
        failed = true
        this.emitError({
          message: 'Failed to get names of addresses because the request to the relayer failed.',
          level: 'silent',
          sendCrashReport: true,
          error: e
        })
        contractsToFetch.forEach(({ address }) => {
          this.#contractNames[address] = {
            address,
            name: null,
            error: 'Request to relayer failed',
            isLoading: false,
            updatedAt: Date.now(),
            retryAfter: PERSIST_FAILED_IN_MS
          }
        })
        // this is just to keep the type safety in case of changes
        return { error: e.message }
      })
    if (failed) {
      this.emitUpdate()
      return
    }

    if ('error' in res) {
      this.emitError({
        message: 'Failed to get names of addresses because the request to the relayer failed.',
        level: 'silent',
        sendCrashReport: true,
        error: new Error(res.error)
      })
      contractsToFetch.forEach(({ address }) => {
        this.#contractNames[address] = {
          address,
          name: null,
          error: 'Request to relayer failed',
          isLoading: false,
          updatedAt: Date.now(),
          retryAfter: PERSIST_FAILED_IN_MS
        }
      })
      this.emitUpdate()
      return
    }

    contractsToFetch.forEach(({ address }) => {
      const foundData = res.contracts?.[address]
      this.#contractNames[address] = foundData?.name
        ? {
            address,
            name: foundData.name,
            isLoading: false,
            updatedAt: Date.now(),
            source: 'relayer'
          }
        : {
            address,
            name: null,
            error: 'Contract name not found',
            isLoading: false,
            updatedAt: Date.now(),
            retryAfter: PERSIST_NOT_FOUND_IN_MS
          }
    })

    this.emitUpdate()
  }

  #shouldSkipGetName(address: string, chainId: bigint): boolean {
    const entry = this.#contractNames[address]
    if (!entry) return false
    if (entry.name) return true
    if (entry.isLoading) return true
    if (
      this.#contractsPendingToBeFetched.some((p) => p.address === address && p.chainId === chainId)
    ) {
      return true
    }
    if (entry.updatedAt && entry.retryAfter) {
      const nextAllowedFetch = entry.updatedAt + entry.retryAfter
      if (Date.now() < nextAllowedFetch) return true
    }
    return false
  }

  getName(_address: string, chainId: bigint) {
    if (!isAddress(_address))
      return this.emitError({
        message: 'Non address passed to ContractNamesController.getName',
        level: 'silent',
        sendCrashReport: true,
        error: new Error(
          `Non-address passed to ContractNamesController.getName: ${_address}, ${chainId}`
        )
      })
    const address = getAddress(_address)
    this.#chainIdByAddress[address] = chainId

    // The registry answers synchronously once loaded, and its names are better
    // than the relayer's, so a hit short-circuits the whole network round trip.
    const dexRouter = this.#getDexRouterEntry(address, chainId)
    if (dexRouter) {
      if (this.#hasDexName(address, dexRouter.name)) return
      this.#contractNames[address] = {
        address,
        name: dexRouter.name,
        isLoading: false,
        updatedAt: Date.now(),
        source: 'dex',
        ...(dexRouter.logoURI ? { logoURI: dexRouter.logoURI } : {})
      }
      this.emitUpdate()
      return
    }

    // Not loaded yet (or expired): kick a load off and let the relayer path run
    // in parallel; #applyDexRoutersToKnownNames upgrades the name if it wins.
    this.#ensureDexRouters().catch(() => {})

    if (this.#shouldSkipGetName(address, chainId)) return

    this.#contractsPendingToBeFetched.push({ address, chainId })
    if (this.#contractNames[address]) {
      this.#contractNames[address].isLoading = true
    } else {
      this.#contractNames[address] = { address, name: null, isLoading: true }
    }

    // if we already have recent fetch, do not add new one
    if (Date.now() - this.#lastTimeScheduledFetch < this.#debounceTime) return

    this.#lastTimeScheduledFetch = Date.now()

    wait(this.#debounceTime)
      .then(() => this.#batchFetchNames())
      .catch((e) => {
        this.emitError({
          message: 'Failed to fetch address name',
          level: 'silent',
          sendCrashReport: true,
          error: e
        })
      })
  }

  toJSON() {
    return {
      ...this,
      ...super.toJSON(),
      contractNames: this.contractNames
    }
  }
}
